/**
 * Theme helpers. The app supports two themes — `"light"` (default, Material-3
 * Google Workspace look) and `"dark"` (activated by toggling the `.dark` class
 * on `<html>`).
 *
 * Resolution order on boot:
 *  1. `localStorage.theme` — user's last explicit choice wins.
 *  2. `window.matchMedia("(prefers-color-scheme: dark)")` — honours OS.
 *  3. Fall back to `"light"`.
 *
 * The `Theme` union is the public contract; all read boundaries (localStorage,
 * the matchMedia query) narrow back into this union before any consumer sees
 * the value. That way `const next: Theme = theme === "dark" ? "light" : "dark"`
 * and friends compile without having to re-narrow in every call site.
 */

export type Theme = "light" | "dark";

const STORAGE_KEY = "theme";

function isTheme(value: unknown): value is Theme {
  return value === "light" || value === "dark";
}

function readStoredTheme(): Theme | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isTheme(raw) ? raw : null;
  } catch {
    // Private-browsing / quota errors — treat as "no preference stored".
    return null;
  }
}

function prefersDarkSystem(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches === true;
  } catch {
    return false;
  }
}

export function getInitialTheme(): Theme {
  const stored = readStoredTheme();
  if (stored !== null) return stored;
  return prefersDarkSystem() ? "dark" : "light";
}

export function setTheme(theme: Theme): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // localStorage denied — best-effort; the class toggle below still runs.
  }
  applyTheme(theme);
}

export function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
}
